import { dateApiFormat } from '@alexis/helpers/date';

import {
  DIRECT_ONLY_PARAM,
  SHOW_FARE_CALENDAR_6_MONTHS_PARAM,
  TEST_FEATURES_PARAM,
} from '@constants/flight';

import { Currency } from '@wegoTypes/currency';
import { FareCalendarPayload } from '@wegoTypes/flights/metasearch/calendar';

export const getSubsequentMonthDateFromCurrentDate = (
  currentDate: Date,
  offset: number,
  isStartDate: boolean,
) => {
  const newDate = new Date(currentDate);
  newDate.setMonth(newDate.getMonth() + offset);
  const year = newDate.getFullYear();
  const month = newDate.getMonth() + 1;

  return dateApiFormat(new Date(year, month - 1, isStartDate ? 1 : 0));
};

export const getFlightFareCalendarParams = (
  isDeparture: boolean,
  legSearch: LegSearch,
  currency: Currency,
  searchParams: URLSearchParams,
): FareCalendarPayload => {
  const departureCode = legSearch.outboundPlace?.code || '';
  const departureCodeType = legSearch.outboundPlace?.type === 'city' ? 'City' : 'Airport';
  const arrivalCode = (legSearch.inboundPlace as OutboundPlace)?.code || '';
  const arrivalCodeType =
    (legSearch.inboundPlace as OutboundPlace)?.type === 'city' ? 'City' : 'Airport';

  const testFeatures = searchParams.get(TEST_FEATURES_PARAM)?.split(',') || [];
  const isFareCalendar6MonthsParam = testFeatures.includes(SHOW_FARE_CALENDAR_6_MONTHS_PARAM);

  const isDirectFlightOnly = searchParams.has(DIRECT_ONLY_PARAM);

  const today = new Date();
  const monthOffsets = isFareCalendar6MonthsParam ? [0] : [0, 1, 2, 3, 4, 5];
  const range = isFareCalendar6MonthsParam ? 7 : 2;
  const fareCalendarDateRanges = monthOffsets.map((offset) => ({
    startDate: getSubsequentMonthDateFromCurrentDate(today, offset, true),
    endDate: getSubsequentMonthDateFromCurrentDate(today, offset + range, false),
  }));

  if (isDeparture) {
    return {
      departureLocations: [
        {
          code: departureCode,
          type: departureCodeType,
        },
      ],
      arrivalLocations: [
        {
          code: arrivalCode,
          type: arrivalCodeType,
        },
      ],
      currencyCode: currency.code,
      dateRanges: fareCalendarDateRanges,
      tripType: 'ONEWAY',
      directOnly: isDirectFlightOnly,
    };
  }

  return {
    departureLocations: [
      {
        code: arrivalCode,
        type: arrivalCodeType,
      },
    ],
    arrivalLocations: [
      {
        code: departureCode,
        type: departureCodeType,
      },
    ],
    currencyCode: currency.code,
    dateRanges: fareCalendarDateRanges,
    tripType: 'ONEWAY',
    directOnly: isDirectFlightOnly,
  };
};

export const isValidDate = (
  date: Date | undefined,
  limitFromMilliseconds?: number,
  limitToMilliseconds?: number,
) => {
  if (!date) {
    return false;
  }
  const time = date.getTime();
  if (limitFromMilliseconds && time < limitFromMilliseconds) {
    return false;
  }
  if (limitToMilliseconds && time > limitToMilliseconds) {
    return false;
  }
  return !isNaN(time);
};

export const parseDateFromInputString = (
  input: string,
  limitFrom: number,
  limitTo: number,
): Date | undefined => {
  const dateFromNewDate = new Date(input);
  if (isValidDate(dateFromNewDate, limitFrom, limitTo)) {
    return dateFromNewDate;
  }

  const parts = input.split(/[/\-\s]+/);

  // passed only day
  if (parts.length === 1) {
    return parseDateFromMonthDayString(parts[0], limitFrom, limitTo);
  }

  // pass dd/mm or mm/dd
  if (parts.length === 2) {
    return (
      parseDateFromMonthDayString(parts[0], limitFrom, limitTo, parts[1]) ||
      parseDateFromMonthDayString(parts[1], limitFrom, limitTo, parts[0])
    );
  }

  // pass year, other cases are accepted by new Date()
  if (parts.length === 3) {
    return (
      // yy/mm/dd
      parseDateFromMonthDayString(parts[2], limitFrom, limitTo, parts[1], parts[0]) ||
      // dd/mm/yy
      parseDateFromMonthDayString(parts[0], limitFrom, limitTo, parts[1], parts[2]) ||
      // yy/dd/mm
      parseDateFromMonthDayString(parts[1], limitFrom, limitTo, parts[2], parts[0]) ||
      // mm/dd/yy
      parseDateFromMonthDayString(parts[1], limitFrom, limitTo, parts[0], parts[2])
    );
  }
};

const monthNames = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
];

const parseDateFromMonthDayString = (
  day: string,
  limitFrom: number,
  limitTo: number,
  month?: string,
  year?: string,
): Date | undefined => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  const parsedDay = parseInt(day);

  if (parsedDay < 1 || parsedDay > 31) {
    return;
  }

  if (month) {
    const parsedMonth = parseInt(month);
    const isMonthNumber = !isNaN(parsedMonth);
    const finalMonth = isMonthNumber
      ? parsedMonth - 1
      : monthNames.findIndex((m) => m.startsWith(month.toLowerCase()));

    if (year) {
      let parsedYear = parseInt(year);
      if (parsedYear < 2000) {
        parsedYear += 2000;
      }

      const chosenYearResult = new Date(parsedYear, finalMonth, parsedDay);
      chosenYearResult.setHours(0, 0, 0, 0);

      if (isValidDate(chosenYearResult, limitFrom, limitTo)) {
        return chosenYearResult;
      } else {
        return;
      }
    }

    const currentYearResult = new Date(currentYear, finalMonth, parsedDay);
    currentYearResult.setHours(0, 0, 0, 0);
    if (isValidDate(currentYearResult, limitFrom, limitTo)) {
      return currentYearResult;
    }

    const nextYearResult = new Date(currentYear + 1, finalMonth, parsedDay);
    nextYearResult.setHours(0, 0, 0, 0);
    if (isValidDate(nextYearResult, limitFrom, limitTo)) {
      return nextYearResult;
    }
  }

  const currentMonthResult = new Date(currentYear, currentMonth, parsedDay);
  currentMonthResult.setHours(0, 0, 0, 0);
  if (isValidDate(currentMonthResult, limitFrom, limitTo)) {
    return currentMonthResult;
  }

  const nextMonthResult = new Date(currentYear, currentMonth + 1, parsedDay);
  nextMonthResult.setHours(0, 0, 0, 0);
  if (isValidDate(nextMonthResult, limitFrom, limitTo)) {
    return nextMonthResult;
  }
};
